/**
 * Centralized error handling
 *
 * Provides consistent error types and handling across the application.
 */

/**
 * Base application error
 */
export class AppError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
    public readonly context?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'AppError';
    Object.setPrototypeOf(this, AppError.prototype);
  }
}

/**
 * API error - errors from backend API calls
 */
export class ApiError extends AppError {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly response?: unknown,
    code?: string
  ) {
    super(message, code);
    this.name = 'ApiError';
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  /**
   * Check if error is a specific HTTP status code
   */
  is(statusCode: number): boolean {
    return this.statusCode === statusCode;
  }

  /**
   * Check if error is unauthorized (401)
   */
  isUnauthorized(): boolean {
    return this.statusCode === 401;
  }

  /**
   * Check if error is forbidden (403)
   */
  isForbidden(): boolean {
    return this.statusCode === 403;
  }

  /**
   * Check if error is not found (404)
   */
  isNotFound(): boolean {
    return this.statusCode === 404;
  }

  /**
   * Check if error is server error (5xx)
   */
  isServerError(): boolean {
    return this.statusCode >= 500 && this.statusCode < 600;
  }
}

/**
 * Validation error - client-side or server-side validation failures
 */
export class ValidationError extends AppError {
  constructor(
    message: string,
    public readonly field?: string,
    public readonly errors?: Record<string, string[]>
  ) {
    super(message, 'VALIDATION_ERROR');
    this.name = 'ValidationError';
    Object.setPrototypeOf(this, ValidationError.prototype);
  }
}

/**
 * Network error - network connectivity issues
 */
export class NetworkError extends AppError {
  constructor(message: string = 'Network request failed') {
    super(message, 'NETWORK_ERROR');
    this.name = 'NetworkError';
    Object.setPrototypeOf(this, NetworkError.prototype);
  }
}

/**
 * Timeout error - request took too long
 */
export class TimeoutError extends AppError {
  constructor(message: string = 'Request timed out') {
    super(message, 'TIMEOUT_ERROR');
    this.name = 'TimeoutError';
    Object.setPrototypeOf(this, TimeoutError.prototype);
  }
}

/**
 * Parse error - failed to parse response
 */
export class ParseError extends AppError {
  constructor(message: string = 'Failed to parse response') {
    super(message, 'PARSE_ERROR');
    this.name = 'ParseError';
    Object.setPrototypeOf(this, ParseError.prototype);
  }
}

/**
 * Check if error is an AppError or subclass
 */
export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError;
}

/**
 * Check if error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Check if error is a ValidationError
 */
export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

/**
 * Check if error is a NetworkError
 */
export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

/**
 * Check if error is a TimeoutError
 */
export function isTimeoutError(error: unknown): error is TimeoutError {
  return error instanceof TimeoutError;
}

/**
 * Classify whether an error is transient (worth retrying).
 * True for transport failures (NetworkError, TimeoutError — no server was reached)
 * and server-side wobble (ApiError with a 5xx status). 4xx and unknown errors are not transient.
 */
export function isTransientError(error: unknown): boolean {
  if (isNetworkError(error) || isTimeoutError(error)) {
    return true;
  }
  if (isApiError(error)) {
    return error.isServerError();
  }
  return false;
}

/**
 * Convert unknown error to AppError
 */
export function toAppError(error: unknown): AppError {
  if (isAppError(error)) {
    return error;
  }

  if (error instanceof Error) {
    return new AppError(error.message, 'UNKNOWN_ERROR', {
      originalError: error.name,
    });
  }

  return new AppError(String(error), 'UNKNOWN_ERROR', { rawError: error });
}

/**
 * Get user-friendly error message
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    if (error.isUnauthorized()) {
      return 'Please log in to continue';
    }
    if (error.isForbidden()) {
      return 'You do not have permission to perform this action';
    }
    if (error.isNotFound()) {
      return 'The requested resource was not found';
    }
    if (error.isServerError()) {
      return 'A server error occurred. Please try again later';
    }
    return 'Something went wrong. Try again, and get in touch if it keeps happening.';
  }

  if (isValidationError(error)) {
    return 'Something went wrong. Try again, and get in touch if it keeps happening.';
  }

  if (isNetworkError(error)) {
    return 'Network connection failed. Please check your internet connection';
  }

  if (error instanceof Error) {
    return 'Something went wrong. Try again, and get in touch if it keeps happening.';
  }

  return 'An unexpected error occurred';
}
